import type { DelimitedText } from "../content/csv/delimited-rows.js";
import { declaredNames, headsColumns } from "./import-column-names.js";

/**
 * Which columns of an imported file hold Chinese words.
 *
 * **A column is chosen by its content and only labelled by its name.** A header
 * match cannot be the requirement. An Anki export often carries no header row
 * at all, a spreadsheet may be headed in a language nobody listed, and any list
 * of names is a list of the headers somebody thought of. The cells decide.
 *
 * **Everything else is dropped here.** The site draws readings, meanings and
 * levels from its own 词典, so a reader's pinyin, definitions, notes and study
 * statistics have nowhere to go. Leaving them at the parser keeps a staged
 * import small and keeps free text out of anything this site stores.
 *
 * **Punctuation is what tells a word from a sentence.** A length limit generous
 * enough to keep every published headword admits most 例句, so the mark at the
 * end of a sentence does the work a limit cannot. See {@link sentenceMark}.
 *
 * **Several columns can qualify and all of them are taken.** A reader holding
 * both 简体 and 繁體 wants both. Merging and deduplicating happens after each
 * candidate has been resolved against the 词典, so 説 and 說 collapse onto the
 * one published headword while 妳 and 你 stay two words.
 */

/** A character of Chinese, which is the test a column is taken on. */
const han = /\p{Script=Han}/u;

/**
 * Punctuation that says a cell is a sentence rather than a word.
 *
 * **This is what tells a word column from a 例句 column, and length is not.** A
 * limit generous enough to keep every published headword is generous enough to
 * admit most sentences. 我今天早上去了图书馆看书 is twelve characters,
 * comfortably inside {@link longestCandidate}, and it is plainly not a word.
 * The mark at the end of one is what says so.
 *
 * **No headword the 词典 publishes holds any of these.** Measured over all
 * 185,090 of them, forty carry punctuation of any kind and every one of those
 * is the interpunct in a transliterated name (切·格瓦拉, 史蒂夫·乔布斯). That
 * character is deliberately absent from this set.
 *
 * A sentence written without its final mark still passes. It then resolves to
 * no headword and is reported as unrecognised, which is visible on the review
 * screen rather than silent in a saved list.
 */
const sentenceMark = /[，。！？、；：《》「」『』…—～“”‘’!?,.;:]/u;

/**
 * The longest a cell may be and still be a word.
 *
 * The outer bound on one candidate, where {@link sentenceMark} is the
 * judgement. It keeps a run of free text carrying no punctuation out of the
 * staged import, and with it the personal notes #565 worries about.
 *
 * Thirty-two, against a longest published headword of nineteen
 * (中央人民政府駐香港特別行政區聯絡辦公室). Half a percent of the section is
 * longer than seven characters and 0.02% is longer than twelve, so a tighter
 * limit would refuse entries this site publishes. The room is for a writing the
 * section does not publish, which is reported as unrecognised rather than
 * dropped for its length.
 */
export const longestCandidate = 32;

/**
 * How many of a column's cells are looked at.
 *
 * Fifty, which is enough to tell a column of words from a column of sentences
 * and few enough that the judgement costs nothing on a file of thousands. A
 * column is taken or left whole, so this is a sample of the evidence rather
 * than a sample of the work.
 */
const sampledCells = 50;

/**
 * What share of a column's cells have to pass for the column to.
 *
 * **Three fifths, which is far looser than the data needs.** Of the 185,090
 * headwords the 词典 publishes, 185,033 hold a Han character, so a column of
 * this site's own export scores 99.97%. An English column scores near nothing.
 * The room between them is for a reader's own list, which may carry a few rows
 * of notes or a blank where they gave up on a word.
 */
const passingShare = 0.6;

/** How many cells of each column the review screen is shown. */
const shownCells = 3;

/** What became of one column. */
export type ImportColumnVerdict = "words" | "notChinese" | "notWords" | "empty";

/** One column of an imported file, and whether its cells are words. */
export interface ImportColumn {
  /** Where it sits in a record, counting from zero. */
  readonly at: number;

  /** What the file calls it, where the file says. */
  readonly name?: string;

  /** The first few cells, so the reader can see what was judged. */
  readonly shown: readonly string[];

  /** Whether its cells are Chinese words, and where not, why not. */
  readonly verdict: ImportColumnVerdict;
}

/**
 * How many characters a cell holds, counting code points.
 *
 * `String.length` counts UTF-16 units, so a headword written with a character
 * outside the basic plane counts double. The 词典 publishes entries under
 * Extension B characters, and a length measured in units would refuse one for
 * being twice as long as it is.
 * @param cell - What the column holds in one record.
 * @returns Its length in characters.
 */
function characters(cell: string): number {
  return Array.from(cell).length;
}

/**
 * Whether one cell reads as a word rather than as a sentence or a note.
 *
 * The judgement a column is scored on, and the one a candidate is taken on. See
 * {@link sentenceMark} for why punctuation carries more of it than length does.
 * @param cell - What a column holds in one record.
 * @returns Whether it could be a headword.
 */
function readsAsWord(cell: string): boolean {
  return (
    han.test(cell) &&
    !sentenceMark.test(cell) &&
    characters(cell) <= longestCandidate
  );
}

/** What one column's cells are, and what they say about it. */
function verdictOf(cells: readonly string[]): ImportColumnVerdict {
  const filled = cells.filter((cell) => cell !== "");

  if (filled.length === 0) {
    return "empty";
  }

  const chinese = filled.filter((cell) => han.test(cell));

  if (chinese.length / filled.length < passingShare) {
    return "notChinese";
  }

  const words = chinese.filter((cell) => readsAsWord(cell));

  return words.length / chinese.length < passingShare ? "notWords" : "words";
}

/** Every column an import holds, with the names and the records to judge it. */
export interface ImportColumnsProperties {
  /** What the reader gave, as `delimitedRows` read it. */
  readonly read: DelimitedText;
}

/** An import's columns, and which of them hold words. */
export interface ImportColumns {
  /** Every column, in the order the file writes them. */
  readonly columns: readonly ImportColumn[];

  /** The records holding values, with a header row taken off where there was one. */
  readonly rows: readonly (readonly string[])[];
}

/**
 * Which columns of an import hold Chinese words, and what the rest are.
 *
 * Every column is reported, taken or not, because the review screen shows the
 * reader what was ignored and why. A reader whose headwords landed in a column
 * the sieve refused can see that on the page instead of guessing.
 * @param properties - The file as it was read.
 * @returns Every column with its verdict, and the records under them.
 */
export function importColumns(
  properties: ImportColumnsProperties,
): ImportColumns {
  const { read } = properties;
  const heads = headsColumns(read.rows);
  const rows = heads ? read.rows.slice(1) : read.rows;
  const names = heads
    ? read.rows[0]
    : declaredNames(read.comments, read.separator);
  const width = rows.reduce((most, row) => Math.max(most, row.length), 0);

  const columns = Array.from({ length: width }, (_, at): ImportColumn => {
    const cells = rows.slice(0, sampledCells).map((row) => row[at] ?? "");
    const name = names?.[at];

    return {
      /*
       * Spread rather than set, because `exactOptionalPropertyTypes` refuses an
       * explicit `undefined` where the property is optional. A file with no
       * header row and no `#columns:` line names none of its columns.
       */
      ...(name === undefined || name === "" ? {} : { name }),
      at,
      shown: cells.filter((cell) => cell !== "").slice(0, shownCells),
      verdict: verdictOf(cells),
    };
  });

  return { columns, rows };
}

/** The columns holding words, which are the ones an import reads from. */
export function wordColumns(
  columns: readonly ImportColumn[],
): readonly ImportColumn[] {
  return columns.filter((column) => column.verdict === "words");
}
